const generateProduct = data => {
    // Generate product from SalesItem/Rix response
    if (data.itemKey) {
        const languageCode = window.Checkout.settings.language_code;
        const localisedItemInfo = _getInfoForLocale(data.localisedCommunications, languageCode);

        try {
            const s3Image = localisedItemInfo.media
                ?.find(({ typeName }) => typeName === 'MAIN_PRODUCT_IMAGE')?.variants
                ?.find(({ quality }) => quality === 'S3').href ?? '';
            const productName = localisedItemInfo.productName || ' ';
            const articleType = data.itemKey.itemType;
            const articleNumber = data.itemKey.itemNo;
            const articleNumberFormatted = _getArticleNumber(articleNumber);
            const designText = (localisedItemInfo.validDesign && localisedItemInfo.validDesign.text) || '';
            const breathTakingItem = data.isBreathTakingItem;
            const productType = (localisedItemInfo.productType && localisedItemInfo.productType.name) || '';
            const isNew = data.isNew;
            const childProducts = data.childItems || false;
            const unitPriceText = _getArticleUnitPriceText(localisedItemInfo);

            const measurements = _getArticleMeasurements(localisedItemInfo);
            const energyLabel = localisedItemInfo?.technicalInformation?.labelUrl ?? false;

            return { s3Image, productName, unitPriceText, articleType, articleNumber, articleNumberFormatted, measurements, designText, breathTakingItem, productType, isNew, childProducts, energyLabel };
        } catch (err) {
            console.error(err); // eslint-disable-line no-console
        }
    } else {
        try {
            const s3Image = data.RetailItemComm.RetailItemImageList.RetailItemImage[2].ImageUrl.$;
            const productName = data.RetailItemComm.ProductName.$;
            const articleType = data.RetailItemComm.ItemType.$;
            const articleNumber = data.RetailItemComm.ItemNo.$.toString();
            const articleNumberFormatted = _getArticleNumber(articleNumber);
            const cabinetNumber = _getCabinetNumber(data);
            const ecoFee = _getEcoFee(data);
            const designText = _getDesignText(data);
            const breathTakingItem = data.RetailItemComm.BreathTakingItem.$;
            const productType = data.RetailItemComm.ProductTypeName.$;
            const onlineSellable = data.RetailItemComm.OnlineSellable.$;
            const isNew = _getIsNew(data);
            const regularPrice = _getPrice(data, 'RegularSalesUnitPrice', false);
            const familyPrice = _getPrice(data, 'IKEAFamilySalesUnitPrice', false);
            const regularPriceWithoutVAT = _getPrice(data, 'RegularSalesUnitPrice', true);
            const familyPriceWithoutVAT = _getPrice(data, 'IKEAFamilySalesUnitPrice', true);
            const childProducts = _getChildParts(data);
            const unitPriceText = _getUnitPriceText(data);

            let measurements = _getMeasurements(data);
            let efficiency = null;
            if (measurements.metric.match(/^[A-F]\+*$/)) {
                efficiency = measurements;
                measurements = null;
            }

            return { s3Image, productName, unitPriceText, articleType, articleNumber, articleNumberFormatted, cabinetNumber, measurements, efficiency, designText, breathTakingItem, productType, onlineSellable, isNew, regularPrice, familyPrice, regularPriceWithoutVAT, familyPriceWithoutVAT, childProducts, ecoFee };
        } catch (err) {
            console.error(err); // eslint-disable-line no-console
        }
    }

    return false;
};

const _getUnitPriceText = data => {
    const showImperial = window.Checkout.settings.show_imperial_product_measurements;
    const showMetric = window.Checkout.settings.show_metric_product_measurements;

    if (showMetric) {
        let unitPriceText = data.RetailItemComm.PriceUnitTextMetric.$;
        if (data.RetailItemComm.ItemPriceUnitFactorMetric) {
            unitPriceText = data.RetailItemComm.ItemPriceUnitFactorMetric.$ + ' ' + unitPriceText;
        }
        return unitPriceText;
    } else if (showImperial) {
        let unitPriceText = data.RetailItemComm.PriceUnitTextImperial.$;
        if (data.RetailItemComm.ItemPriceUnitFactorImperial) {
            unitPriceText = data.RetailItemComm.ItemPriceUnitFactorImperial.$ + ' ' + unitPriceText;
        }
        return unitPriceText;
    }
    return '';
};

const _formatProduct = data => {
    const articleNumber = data.itemNo;
    const articleType = data.type;
    const quantity = data.quantity;
    let regularPrice = data.totalPrice.regularPrice;
    let familyPrice = data.totalPrice.familyPrice;
    const hasPromotionPrice = !!regularPrice.discountList;
    const price = [];

    if (hasPromotionPrice) {
        const formattedPromotionPrice = {
            exclTax: regularPrice.exclTax, // Set the unit price with discount to the total since data is missing
            inclTax: regularPrice.inclTax, // Set the unit price with discount to the total since data is missing
            totalExclTax: regularPrice.exclTax,
            totalInclTax: regularPrice.inclTax,
            type: 'PromotionPrice',
        };
        price.push(formattedPromotionPrice);
    }

    if (regularPrice) {
        const formattedRegularPrice = {
            exclTax: regularPrice.perItemPrice.exclTax,
            inclTax: regularPrice.perItemPrice.inclTax,
            totalExclTax: regularPrice.exclTax,
            totalInclTax: regularPrice.inclTax,
            type: 'RegularSalesPrice',
        };
        price.push(formattedRegularPrice);
        regularPrice = formattedRegularPrice;
    }

    if (familyPrice) {
        const formattedFamilyPrice = {
            ValidFromDateTime: familyPrice.perItemPrice.validFrom ? _convertToLocalDate(familyPrice.perItemPrice.validFrom) : null,
            ValidToDateTime: familyPrice.perItemPrice.validTo ? _convertToLocalDate(familyPrice.perItemPrice.validTo) : null,
            exclTax: hasPromotionPrice ? familyPrice.exclTax : familyPrice.perItemPrice.exclTax,
            inclTax: hasPromotionPrice ? familyPrice.inclTax : familyPrice.perItemPrice.inclTax,
            totalExclTax: familyPrice.exclTax,
            totalInclTax: familyPrice.inclTax,
            type: 'FamilyPrice',
        };
        price.push(formattedFamilyPrice);
        familyPrice = formattedFamilyPrice;
    }

    return { articleNumber, articleType, price, quantity, regularPrice, familyPrice };
};

const formatProducts = data => {
    const result = {};
    if (typeof data.length !== 'undefined') {
        // The object contains more than one products and is returned in as an array by the webservice.
        for (let i = 0; i < data.length; i++) {
            const product = data[i];
            if (product.totalPrice) { // Check if the item format matches the new checkout API
                result[product.itemNo] = _formatProduct(product);
            } else {
                let articleNumber;
                // Adds support for 3 APIs, IRW, ICP and ONE
                if (typeof data[i].ItemNo !== 'undefined') {
                    articleNumber = data[i].ItemNo.$;
                    result[articleNumber] = {};
                    result[articleNumber].icpId = data[i].Id ? data[i].Id.$ : false;
                    result[articleNumber].articleNumber = articleNumber;
                    result[articleNumber].articleType = data[i].ItemType.$;
                    result[articleNumber].quantity = data[i].ItemQty ? data[i].ItemQty.$ : data[i].SalesOrderQty.$;
                } else {
                    articleNumber = data[i].itemNumber;

                    result[articleNumber] = {};
                    result[articleNumber].articleNumber = articleNumber;
                    result[articleNumber].articleType = data[i].itemType;
                    result[articleNumber].quantity = data[i].quantity;
                    result[articleNumber].price = data[i].prices;
                    result[articleNumber].link = data[i]._links.self[0].href;
                }

                // Find the sort sequence and save it if available.
                if (typeof data[i].SortRelevanceList !== 'undefined') {
                    const sortRelevanceList = data[i].SortRelevanceList.SortRelevance;
                    for (let sortId = 0; sortId < sortRelevanceList.length; sortId++) {
                        if (sortRelevanceList[sortId].hasOwnProperty('SortType') && sortRelevanceList[sortId].SortType.$ === 'sequence') {
                            result[articleNumber].sortId = sortRelevanceList[sortId].SortNo.$;
                        }
                    }
                }
            }
        }
    } else if (data.ItemNo) {
        // The object contains only one product, is returned as a object by webservice...for some reason.
        const articleNumber = data.ItemNo.$;
        result[articleNumber] = {};

        result[articleNumber].articleNumber = articleNumber;
        result[articleNumber].articleType = data.ItemType.$;
        result[articleNumber].quantity = data.ItemQty.$;

        // Find the sort sequence and save it if available.
        if (typeof data.SortRelevanceList !== 'undefined') {
            const sortRelevanceList = data.SortRelevanceList.SortRelevance;
            for (let sortId = 0; sortId < sortRelevanceList.length; sortId++) {
                if (sortRelevanceList[sortId].hasOwnProperty('SortType') && sortRelevanceList[sortId].SortType.$ === 'sequence') {
                    result[articleNumber].sortId = sortRelevanceList[sortId].SortNo.$;
                }
            }
        }
    }
    return result;
};

const getQuantityOptions = (fixedQuantity) => {
    const qty = [];
    const maxQuantity = fixedQuantity ? fixedQuantity : window.Checkout.settings.max_quantity;
    for (let i = 1; i <= maxQuantity; i++) {
        qty[i - 1] = {
            name: i,
            value: i,
        };
    }
    return qty;
};

/**
 * Retrieve if product is new.
 * @param {object} data
 */
const _getIsNew = (data) => {
    if (typeof data.RetailItemComm.NewsType !== 'undefined') {
        return data.RetailItemComm.NewsType.$;
    }
    return false;
};

/**
 * Formats article number to add the dots [.] after 3 and 6 characters.
 * @param {object} data
 */
const _getArticleNumber = (data) => {
    return data.match(/\d{1,3}/g).join('.');
};

/**
 * Get cabinet number for the article. Not always available
 * @param {object} data
 */
const _getCabinetNumber = (data) => {
    if (typeof data.RetailItemComm.PresentationGroupCodeEn !== 'undefined') {
        return data.RetailItemComm.PresentationGroupCodeEn.$;
    }
    return '';
};

/**
 * Get any ecological fees for the article. Not always available
 * @param {object} data
 */
const _getEcoFee = data => {
    if (typeof data.RetailItemComm.RetailItemFeeList !== 'undefined') {
        if (Array.isArray(data.RetailItemComm.RetailItemFeeList.RetailItemFee)) {
            const fees = [];
            data.RetailItemComm.RetailItemFeeList.RetailItemFee.forEach((fee) => {
                fees.push({
                    type: fee.FeeType.$,
                    price: fee.FeeAmount.$,
                });
            });
            return fees;
        }

        return [{
            type: data.RetailItemComm.RetailItemFeeList.RetailItemFee.FeeType.$,
            price: data.RetailItemComm.RetailItemFeeList.RetailItemFee.FeeAmount.$,
        }];
    }
    return false;
};

/**
 * Get measurements for the article. Not always available
 * @param {object} data
 */
const _getMeasurements = (data) => {
    let measurements = {
        metric: '',
        imperial: '',
    };

    if (typeof data.RetailItemComm.ItemMeasureReferenceTextMetric !== 'undefined') {
        measurements.metric = data.RetailItemComm.ItemMeasureReferenceTextMetric.$;
    }
    if (typeof data.RetailItemComm.ItemMeasureReferenceTextImperial !== 'undefined') {
        measurements.imperial = data.RetailItemComm.ItemMeasureReferenceTextImperial.$;
    }
    // Added to handle response from SalesItem/RIX
    if (data.measurements && data.measurements.referenceMeasurements) {
        measurements = data.measurements.referenceMeasurements[0];
    }

    return measurements;
};

/**
 * Get design text (eg color), not always available
 * @param {object} data
 */
const _getDesignText = (data) => {
    if (typeof data.RetailItemComm.ValidDesignText !== 'undefined') {
        return data.RetailItemComm.ValidDesignText.$;
    }
    return '';
};

/**
 * Get price. If article has only one price API will return an object, if not it will return
 * multiple prices in the form of an array. If the price type is not available it will return false.
 * @param {object} data
 * @param {string} priceType
 * @param {boolean} excludeVAT
 */
const _getPrice = (data, priceType, excludeVAT) => {
    const result = {
        'price': null,
        'currency': null,
        'ValidToDateTime': null,
        'newLowerPrice': null,
        'oldPrice': null,
        'tro': null,
    };
    const retailItemCommPrice = data.RetailItemComm.RetailItemCommPriceList.RetailItemCommPrice;

    if (typeof retailItemCommPrice.length === 'undefined' && priceType === 'RegularSalesUnitPrice') {
        if (excludeVAT && retailItemCommPrice.PriceExclTax) {
            result.price = retailItemCommPrice.PriceExclTax.$;
        } else {
            result.price = retailItemCommPrice.Price.$;
        }

        result.currency = retailItemCommPrice.CurrencyCode.$;

        return result;
    }

    for (let i = 0; i < retailItemCommPrice.length; i++) {
        if (retailItemCommPrice[i].RetailPriceType.$ === priceType) {
            if (excludeVAT && retailItemCommPrice[i].PriceExclTax) {
                result.price = retailItemCommPrice[i].PriceExclTax.$;
            } else {
                result.price = retailItemCommPrice[i].Price.$;
            }
            result.currency = retailItemCommPrice[i].CurrencyCode.$;

            if (typeof retailItemCommPrice[i].ValidToDateTime !== 'undefined') {
                const toDate = new Date(retailItemCommPrice[i].ValidToDateTime.$.replace('Z', ''));
                result.ValidToDateTime = toDate.toLocaleDateString(window.Checkout.settings.locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });
            }

            if (typeof retailItemCommPrice[i].ReasonCode !== 'undefined' && retailItemCommPrice[i].ReasonCode.$ === 'NewLowerPrice') {
                result.newLowerPrice = true;
            }

            if (typeof retailItemCommPrice[i].ReasonCode !== 'undefined' && retailItemCommPrice[i].ReasonCode.$ === 'TimeRestrictedOffer') {
                result.tro = true;
                // TODO: i + 1 doesn't seem safe and might break in the future, should be refactored and saving the old price before this.
                // Once this had been refactored, the check if retailItemCommPrice[i + 1] exists can be removed.
                result.oldPrice = excludeVAT && retailItemCommPrice[i + 1] && retailItemCommPrice[i + 1].PriceExclTax ? retailItemCommPrice[i + 1].PriceExclTax.$ : retailItemCommPrice[i + 1].Price.$;
            }


            return result;
        }
    }

    return false;
};

const _getChildParts = (data) => {
    if (typeof data.RetailItemComm.RetailItemCommChildList === 'undefined') {
        return false;
    }

    if (data.RetailItemComm.RetailItemCommChildList.RetailItemCommChild instanceof Array) {
        return data.RetailItemComm.RetailItemCommChildList.RetailItemCommChild;
    }

    // Convert to array as API return object if only one element, but array if multiple
    if (data.RetailItemComm.RetailItemCommChildList.RetailItemCommChild instanceof Object) {
        return [data.RetailItemComm.RetailItemCommChildList.RetailItemCommChild];
    }

    return false;
};
const _convertToLocalDate = date => {
    return new Date(date).toLocaleDateString(window.Checkout.settings.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

// SalesItem/RIX functions
const _getInfoForLocale = (localisedCommunications, languageCode) => {
    return localisedCommunications && localisedCommunications.find(localisedCommunication => localisedCommunication.languageCode === languageCode);
};
const _getArticleMeasurements = article => {
    let measurements = {
        metric: '',
        imperial: '',
    };
    if (article.measurements && article.measurements.referenceMeasurements) {
        measurements = article.measurements.referenceMeasurements[0];
    }
    return measurements;
};
export const _getArticleUnitPriceText = article => {
    const showImperial = window.Checkout.settings.show_imperial_product_measurements;
    const showMetric = window.Checkout.settings.show_metric_product_measurements;

    if (article?.displayUnit?.unitInformation && showMetric) {
        const { unitMetric, unitMetricValue } = article.displayUnit.unitInformation;

        return (unitMetricValue ? `${unitMetricValue} ` : '') + unitMetric;
    } else if (article?.displayUnit?.unitInformation && showImperial) {
        const { unitImperial, unitImperialValue } = article.displayUnit.unitInformation;

        return (unitImperialValue ? `${unitImperialValue} ` : '') + unitImperial;
    }
    return '';
};

export {
    generateProduct,
    formatProducts,
    getQuantityOptions,
};
